/**
 * Privacy mode hook and exit button.
 *
 * When active, the parent (App.tsx) selectively hides chat, status bar,
 * agent ticker, settings, and other UI — leaving only the orb and
 * mute controls visible. Voice interaction continues normally.
 *
 * This module provides:
 * - usePrivacyMode hook (state + localStorage persistence)
 * - PrivacyExitButton — a subtle lock icon in the corner to exit privacy mode
 */

import { useCallback, useEffect, useState } from 'react';
import { readString, writeString } from '../utils/storage';
import styles from './PrivacyMode.module.css';

const STORAGE_KEY = 'voice-ai-privacy-mode';

// ── Hook for Privacy Mode State ──

/**
 * Hook to manage privacy mode state with localStorage persistence.
 */
export function usePrivacyMode() {
  const [isActive, setIsActive] = useState(() => readString(STORAGE_KEY) === 'true');

  const toggle = useCallback(() => {
    setIsActive((prev) => {
      const next = !prev;
      writeString(STORAGE_KEY, next ? 'true' : 'false');
      return next;
    });
  }, []);

  const dismiss = useCallback(() => {
    setIsActive(false);
    writeString(STORAGE_KEY, 'false');
  }, []);

  // Sync with storage changes from other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setIsActive(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return { isActive, toggle, dismiss };
}

// ── Privacy Exit Button ──

interface PrivacyExitButtonProps {
  /** Called when the user taps the exit button */
  onExit: () => void;
}

/**
 * A subtle lock icon in the top-left corner that exits privacy mode.
 * Small and unobtrusive — not obvious to onlookers but discoverable for the user.
 */
export function PrivacyExitButton({ onExit }: PrivacyExitButtonProps) {
  return (
    <button
      className={styles.exitButton}
      onClick={onExit}
      aria-label="Exit privacy mode"
    >
      🔒
    </button>
  );
}
